import { Locale } from '@/lib/i18n/dictionaries';

export type Environment = 'prod' | 'stage' | 'dev';
/**
 * Single source of truth for all `process.env` reads.
 * Properties are getters so mutations in tests propagate correctly.
 * See README → "Accessing environment variables in code" for usage.
 */
export const env = {
  get nodeEnv() {
    return process.env.NODE_ENV;
  },
  get nextPhase() {
    return process.env.NEXT_PHASE;
  },
  get nextRuntime() {
    return process.env.NEXT_RUNTIME;
  },

  // Public
  get publicEnv() {
    return process.env.NEXT_PUBLIC_ENV as Environment;
  },
  get publicLocale() {
    return (process.env.NEXT_PUBLIC_LOCALE || 'en') as Locale;
  },
  get publicDomain() {
    return process.env.NEXT_PUBLIC_DOMAIN;
  },
  get publicStrapiApiUrl() {
    return process.env.NEXT_PUBLIC_STRAPI_API_URL;
  },
  get publicStrapiAdminUrl() {
    return process.env.NEXT_PUBLIC_STRAPI_ADMIN_URL;
  },
  get publicPosthogApiKey() {
    return process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
  },
  get publicIgnoreDataTrackerLogs() {
    return process.env.NEXT_PUBLIC_IGNORE_DATA_TRACKER_LOGS;
  },
  get publicTermlyWebsiteUuid() {
    return process.env.NEXT_PUBLIC_TERMLY_WEBSITE_UUID;
  },
  get prefetch() {
    return process.env.PREFETCH;
  },

  // Server-only
  get strapiApiToken() {
    return process.env.STRAPI_API_TOKEN;
  },
  get strapiApiUrl() {
    return process.env.STRAPI_API_URL;
  },
  get internalSelfUrl() {
    return process.env.INTERNAL_SELF_URL;
  },
  get internalStrapiApiUrl() {
    return process.env.INTERNAL_STRAPI_API_URL;
  },
  get revalidationSecret() {
    return process.env.REVALIDATION_SECRET;
  },
  get internalRevalidationSecret() {
    return process.env.INTERNAL_REVALIDATION_SECRET;
  },
  get instatusSecret() {
    return process.env.INSTATUS_SECRET;
  },
  get termlyApiKey() {
    return process.env.TERMLY_API_KEY;
  },

  // CDN
  get cfZoneId() {
    return process.env.CF_ZONE_ID;
  },
  get cfCachePurgeApiToken() {
    return process.env.CF_CACHE_PURGE_API_TOKEN;
  },
  get aliyunAccessKeyId() {
    return process.env.ALIYUN_ACCESS_KEY_ID;
  },
  get aliyunAccessKeySecret() {
    return process.env.ALIYUN_ACCESS_KEY_SECRET;
  },
  get revalidationPodUrls() {
    return process.env.REVALIDATION_POD_URLS;
  },

  // K8s
  get kubernetesNamespace() {
    return process.env.KUBERNETES_NAMESPACE;
  },
  get kubernetesServiceHost() {
    return process.env.KUBERNETES_SERVICE_HOST;
  },
  get kubernetesServicePort() {
    return process.env.KUBERNETES_SERVICE_PORT;
  },
  get appSelector() {
    return process.env.APP_SELECTOR;
  },
  get useHttps() {
    return process.env.USE_HTTPS;
  },

  // CI
  get ci() {
    return process.env.CI;
  },
  get githubActions() {
    return process.env.GITHUB_ACTIONS;
  },
  get gitlabCi() {
    return process.env.GITLAB_CI;
  },
  get bitbucketBuildNumber() {
    return process.env.BITBUCKET_BUILD_NUMBER;
  },
  get isTestEnv() {
    return (
      typeof process.env.JEST_WORKER_ID !== 'undefined' ||
      process.env.NODE_ENV === 'test'
    );
  },
  get stagingProdBridgeApiToken() {
    // Used to view prod content on staging env
    if (this.publicEnv === 'prod') {
      return this.strapiApiToken;
    }

    return process.env.STRAPI_STAGING_PROD_BRIDGE_API_TOKEN;
  },
  get isVisualTest() {
    return process.env.VISUAL_TESTS === 'true';
  },
  get isIsolatedBuild() {
    return process.env.ISOLATED_BUILD === 'true';
  },
  get shouldUseSidenavDesign() {
    return process.env.NEXT_PUBLIC_SIDENAV_DESIGN === 'true';
  },
};
